<template>
  <div class="exchange-products">
    <h2>兑换商品管理</h2>
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <span>商品列表</span>
          <el-button type="primary" @click="showCreateDialog">
            <el-icon><Plus /></el-icon>
            新增商品
          </el-button>
        </div>
      </template>
      <div class="product-list-content">
        <!-- 搜索区域 -->
        <div class="search-area">
          <el-input
            v-model="searchForm.keyword"
            placeholder="请输入商品名称"
            prefix-icon="Search"
            clearable
            class="search-input"
          />
          <div class="search-filters">
            <el-select v-model="searchForm.product_type" placeholder="商品类型" clearable style="width: 150px;">
              <el-option label="实物商品" value="physical" />
              <el-option label="权益商品" value="benefit" />
            </el-select>
            <el-select v-model="searchForm.status" placeholder="状态" clearable style="width: 120px;">
              <el-option label="上架" value="active" />
              <el-option label="下架" value="inactive" />
            </el-select>
            <el-button type="primary" @click="handleSearch">
              <el-icon><Search /></el-icon>
              搜索
            </el-button>
          </div>
        </div>

        <!-- 数据表格 -->
        <el-table
          v-loading="loading"
          :data="productList"
          stripe
          style="width: 100%"
          class="product-table"
          :row-key="row => row.id"
        >
          <el-table-column label="商品信息" width="380">
            <template #default="scope">
              <div class="product-info-column">
                <div class="product-name" show-overflow-tooltip>
                  {{ scope.row.name }}
                </div>
                <div class="product-meta">
                  <el-tag :type="scope.row.product_type === 'physical' ? 'primary' : 'success'" size="small">
                    {{ scope.row.product_type === 'physical' ? '实物商品' : '权益商品' }}
                  </el-tag>
                  <el-tag :type="scope.row.status === 'active' ? 'success' : 'danger'" size="small" style="margin-left: 8px;">
                    {{ scope.row.status === 'active' ? '已上架' : '已下架' }}
                  </el-tag>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="价格/积分" width="180">
            <template #default="scope">
              <div>价格：{{ scope.row.price }} 元</div>
              <div>积分：{{ scope.row.points_required }} 分</div>
            </template>
          </el-table-column>
          <el-table-column prop="stock" label="库存数量" width="100" />
          <el-table-column prop="is_limit_purchase" label="是否限购" width="120">
            <template #default="scope">
              <el-tag :type="scope.row.is_limit_purchase ? 'warning' : 'success'">
                {{ scope.row.is_limit_purchase ? '是' : '否' }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="280" fixed="right">
            <template #default="scope">
              <el-button
                type="primary"
                size="small"
                @click="showEditDialog(scope.row)"
              >
                <el-icon><Edit /></el-icon>
                编辑
              </el-button>
              <el-button
                :type="scope.row.status === 'active' ? 'danger' : 'success'"
                size="small"
                @click="handleStatusChange(scope.row)"
              >
                {{ scope.row.status === 'active' ? '下架' : '上架' }}
              </el-button>
              <el-button
                type="danger"
                size="small"
                @click="handleDelete(scope.row)"
              >
                <el-icon><Delete /></el-icon>
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-area">
          <el-pagination
            :current-page="pagination.currentPage"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 创建/编辑商品抽屉 -->
    <el-drawer
      v-model="dialogVisible"
      :title="dialogTitle"
      direction="rtl"
      size="1000px"
      :before-close="handleDialogClose"
      destroy-on-close
    >
      <div class="product-form-content">
        <!-- 自定义标签栏 - 放在el-tabs外面，直接作为product-form-content的子元素 -->
        <div class="custom-tabs">
          <div
            v-for="item in tabs"
            :key="item.name"
            :class="['custom-tab', { active: activeTab === item.name }]"
            @click="activeTab = item.name"
          >
            {{ item.label }}
          </div>
        </div>
        
        <!-- 标签页内容区域，设置为可滚动 -->
        <div class="tabs-content-wrapper">
          <el-tabs v-model="activeTab" type="card" class="no-tabs-header">
            <template #tabs>
              <!-- 隐藏默认标签 -->
            </template>
            <!-- 基础信息标签页 -->
            <el-tab-pane label="商品基础信息" name="basic">
              <el-form :model="formData" :rules="formRules" ref="formRef" label-width="120px" class="product-form">
                <el-form-item label="商品类型" required>
                <el-select v-model="formData.product_type" placeholder="请选择商品类型" :disabled="isEditMode">
                  <el-option label="实物商品" value="physical" />
                  <el-option label="权益商品" value="benefit" />
                </el-select>
                <div v-if="isEditMode" class="form-tip">
                  商品类型创建后不允许修改
                </div>
              </el-form-item>
              
              <!-- 封面图 -->
              <el-form-item label="封面图" prop="image" required>
                <!-- 图片大小提示 -->
                <el-alert
                  title="图片上传提示"
                  type="info"
                  :closable="false"
                  show-icon
                  style="margin-bottom: 16px;"
                >
                  <template #default>
                    <div>基于当前服务器配置，建议您将上传的图片大小控制在200KB以下，以确保页面加载速度和最佳显示效果。</div>
                  </template>
                </el-alert>
                
                <!-- 只有当没有图片时才显示上传按钮 -->
                <div v-if="!formData.image" class="upload-button-container">
                  <el-upload
                    :http-request="customImageUpload"
                    list-type="picture-card"
                    :limit="1"
                    :on-error="handleImageUploadError"
                    :file-list="imageFileList"
                    :auto-upload="true"
                  >
                    <el-icon><Plus /></el-icon>
                    <template #tip>
                      <div class="el-upload__tip">
                        仅支持 JPG、PNG 格式，不超过 2MB
                      </div>
                    </template>
                  </el-upload>
                  <div class="image-required-tip">
                    请上传商品封面图
                  </div>
                </div>
                
                <!-- 显示已上传的图片 -->
                <div v-else class="uploaded-image-container">
                  <el-image
                    :src="formData.image"
                    style="width: 100px; height: 100px;"
                    fit="cover"
                  />
                  <el-button type="text" @click="removeImage" style="margin-top: 10px; color: #f56c6c;">
                    更换图片
                  </el-button>
                </div>
              </el-form-item>
              
              <!-- 商品滚图 -->
              <el-form-item label="商品滚图">
                <el-upload
                  :http-request="customImagesUpload"
                  list-type="picture-card"
                  :multiple="true"
                  :on-error="handleImageUploadError"
                  :on-remove="handleImageRemove"
                  :file-list="imagesFileList"
                  :auto-upload="true"
                  :on-preview="handlePictureCardPreview"
                >
                  <el-icon><Plus /></el-icon>
                  <template #tip>
                    <div class="el-upload__tip">
                      支持多张 JPG、PNG 格式，每张不超过 2MB
                    </div>
                  </template>
                </el-upload>
                <el-dialog v-model="previewVisible" title="预览图片">
                  <img style="width: 100%" :src="dialogImageUrl" alt="预览图片" />
                </el-dialog>
                <div class="form-tip">
                  已上传 {{ imagesFileList.length }} 张图片
                </div>
              </el-form-item>
              
              <!-- 商品标题 -->
              <el-form-item label="商品标题" required>
                <el-input v-model="formData.name" placeholder="请输入商品标题" maxlength="255" show-word-limit />
              </el-form-item>
              

              
              <!-- 商品简介 -->
              <el-form-item label="商品简介">
                <el-input
                  v-model="formData.intro"
                  type="textarea"
                  :rows="3"
                  placeholder="请输入商品简介"
                />
              </el-form-item>
              
              <!-- 库存数量 -->
              <el-form-item label="库存数量" required>
                <el-input-number v-model="formData.stock" :min="0" placeholder="请输入库存数量" />
              </el-form-item>
              
              <!-- 商品价格 -->
              <el-form-item label="商品价格" required>
                <el-input-number v-model="formData.price" :min="0" :precision="2" :step="0.01" placeholder="请输入商品价格" />
              </el-form-item>
              
              <!-- 权益商品有效期设置 -->
              <template v-if="formData.product_type === 'benefit'">
                <el-form-item label="领取后有效期" required>
                  <el-row :gutter="10" align="middle">
                    <el-col :span="12">
                      <el-input-number 
                        v-model="formData.validity_days" 
                        :min="1" 
                        :precision="0" 
                        :step="1"
                        placeholder="请输入天数"
                      />
                    </el-col>
                    <el-col :span="12">
                      <span class="validity-unit">天</span>
                    </el-col>
                  </el-row>
                  <div class="form-help-text">
                    商品被领取后，将在指定天数后失效
                  </div>
                </el-form-item>
              </template>
              
              <!-- 上下架设置 -->
              <el-form-item label="上下架设置" required>
                <el-select v-model="formData.status" placeholder="请选择上下架状态">
                  <el-option label="上架" value="active" />
                  <el-option label="下架" value="inactive" />
                </el-select>
              </el-form-item>
              
              <!-- 详情编辑 -->
              <el-form-item label="详情编辑" required>
                <div class="rich-text-editor">
                  <Toolbar
                    :editor="editor"
                    :default-config="toolbarConfig"
                    :mode="mode"
                  />
                  <Editor
                    v-model="formData.description"
                    :default-config="editorConfig"
                    :mode="mode"
                    @onCreated="handleCreated"
                  />
                </div>
              </el-form-item>
            </el-form>
          </el-tab-pane>
          
          <!-- 兑换条件标签页 -->
        <el-tab-pane label="兑换条件设置" name="exchange">
          <el-form :model="formData" label-width="120px" class="product-form">
            <el-form-item label="可购会员身份">
              <el-select
                v-model="formData.available_member_levels"
                placeholder="请选择可购会员身份"
                multiple
                :loading="loadingMemberLevels"
              >
                <el-option
                  v-for="level in memberLevels"
                  :key="level.id"
                  :label="level.level_name"
                  :value="String(level.id)"
                />
              </el-select>
       
            </el-form-item>
              
              <el-form-item label="是否限购" required>
                <el-switch
                  v-model="formData.is_limit_purchase"
                  active-color="#13ce66"
                  inactive-color="#ff4949"
                />
              </el-form-item>
              
              <el-form-item label="限购数量" v-if="formData.is_limit_purchase" required>
                <el-input-number v-model="formData.limit_purchase_count" :min="1" placeholder="请输入限购数量" />
              </el-form-item>
              
              <el-form-item label="积分数量" required>
                <el-input-number v-model="formData.points_required" :min="0" placeholder="请输入所需积分数量" />
              </el-form-item>
            </el-form>
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>
      
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">
            {{ isEditMode ? '保存修改' : '创建商品' }}
          </el-button>
        </div>
      </template>
    </el-drawer>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Plus, Edit, Check, Remove, Delete } from '@element-plus/icons-vue';
import { Editor, Toolbar } from '@wangeditor/editor-for-vue';
import '@wangeditor/editor/dist/css/style.css';
import * as productApi from '../../api/exchangeProducts';
import { fetchMemberLevelOptions } from '../../api/member';

export default {
  name: 'ExchangeProducts',
  components: {
    Search,
    Plus,
    Edit,
    Check,
    Remove,
    Delete,
    Editor,
    Toolbar
  },
  setup() {
    // 搜索表单
    const searchForm = reactive({
      keyword: '',
      product_type: '',
      status: ''
    });

    // 加载状态
    const loading = ref(false);

    // 商品列表数据
    const productList = ref([]);

    // 分页数据
    const pagination = reactive({
      currentPage: 1,
      pageSize: 10,
      total: 0
    });
    
    // 抽屉相关
    const dialogVisible = ref(false);
    const dialogTitle = ref('新增商品');
    const isEditMode = ref(false);
    const activeTab = ref('basic');
    const formRef = ref(null);
    
    // 标签页数据
    const tabs = [
      { label: '商品基础信息', name: 'basic' },
      { label: '兑换条件设置', name: 'exchange' }
    ];
    
    // 表单验证规则
    const formRules = {
      image: [{ required: true, message: '请上传商品封面图', trigger: 'change' }]
    };
    
    // 表单数据
    const formData = reactive({
      name: '',
      intro: '',
      description: '',
      price: 0,
      original_price: 0,
      category_id: null,
      image: '',
      images: [],
      product_type: 'physical',
      validity_days: 1,
      status: 'active',
      sort_order: 0,
      stock: 0,
      sold_count: 0,
      need_verification: false,
      support_shipping: true,
      is_default: true,
      goods_id: null,
      available_member_levels: [],
      is_limit_purchase: false,
      limit_purchase_count: 1,
      points_required: 0
    });

    // 会员等级列表
    const memberLevels = ref([]);
    // 是否正在加载会员等级
    const loadingMemberLevels = ref(false);
    
    // 文件上传相关
    const imageFileList = ref([]);
    const imagesFileList = ref([]);
    const previewVisible = ref(false);
    const dialogImageUrl = ref('');

    // 富文本编辑器配置
    const editor = ref(null);
    const mode = ref('default');
    const toolbarConfig = ref({
      excludeKeys: ['fullScreen', 'code', 'codeBlock']
    });
    const editorConfig = ref({
      placeholder: '请输入商品详情',
      MENU_CONF: {
        uploadImage: {
          server: '/manage/upload/image',
          fieldName: 'file',
          maxFileSize: 2 * 1024 * 1024,
          allowedFileTypes: ['image/jpg', 'image/jpeg', 'image/png'],
          // 自定义上传图片
          customUpload: async (file, insertFn) => {
            try {
              const formData = new FormData();
              formData.append('file', file);
              
              // 获取API基础URL和认证token
              const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
              const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
              const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
              const authToken = localStorage.getItem('token');
              
              // 直接使用axios而不是全局request，避免响应拦截器影响
              const axios = await import('axios');
              const response = await axios.default({
                url: `${apiBaseUrl}/manage/upload/image`,
                method: 'post',
                data: formData,
                headers: {
                  'Content-Type': 'multipart/form-data',
                  'Authorization': `Bearer ${authToken}`
                },
                timeout: 15000
              });
              
              const res = response.data;
              // 处理wangEditor格式响应
              if (res.errno === 0 && res.data && res.data.length > 0) {
                let imgUrl = res.data[0].url;
                // 确保返回的是完整的绝对路径
                if (imgUrl && !imgUrl.startsWith('http://') && !imgUrl.startsWith('https://')) {
                  imgUrl = `${baseDomain}${imgUrl}`;
                }
                insertFn(imgUrl); // 插入图片
              } else {
                ElMessage.error('图片上传失败');
              }
            } catch (error) {
              console.error('图片上传失败:', error);
              ElMessage.error('图片上传失败');
            }
          }
        }
      }
    });

    // 编辑器创建完成事件
    const handleCreated = (editorInstance) => {
      editor.value = editorInstance;
    };

    // 加载商品列表
    const loadProducts = async () => {
      loading.value = true;
      try {
        const params = {
          page: pagination.currentPage,
          per_page: pagination.pageSize,
          keyword: searchForm.keyword,
          product_type: searchForm.product_type,
          status: searchForm.status
        };
        const response = await productApi.fetchExchangeProductsList(params);
        if (response.code === 200) {
          // 直接使用response.data，因为它是一个LengthAwarePaginator实例
          productList.value = response.data.data;
          pagination.total = response.data.total;
        } else {
          ElMessage.error('获取商品列表失败');
        }
      } catch (error) {
        ElMessage.error('获取商品列表失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    // 搜索商品
    const handleSearch = () => {
      pagination.currentPage = 1;
      loadProducts();
    };
    
    // 显示创建弹窗
    const showCreateDialog = async () => {
      resetForm();
      dialogTitle.value = '新增商品';
      isEditMode.value = false;
      
      // 确保会员等级列表已加载
      if (memberLevels.value.length === 0) {
        await fetchMemberLevels();
      }
      
      dialogVisible.value = true;
      activeTab.value = 'basic';
    };
    
    // 显示编辑弹窗
    const showEditDialog = async (row) => {
      resetForm();
      dialogTitle.value = '编辑商品';
      isEditMode.value = true;
      
      // 确保会员等级列表已加载
      if (memberLevels.value.length === 0) {
        await fetchMemberLevels();
      }
      
      try {
        const response = await productApi.fetchExchangeProductDetail(row.id);
        console.log('完整的API响应:', response);
        console.log('API响应数据结构:', Object.keys(response.data));
        
        if (response.code === 200) {
          const productData = response.data;
          console.log('productData结构:', Object.keys(productData));
          
          const product = productData.product;
          console.log('product结构:', Object.keys(product));
          console.log('product.images原始值:', product.images);
          console.log('product.images类型:', typeof product.images);
          console.log('product.images是否为数组:', Array.isArray(product.images));
          
          // 处理封面图
          if (product.image) {
            imageFileList.value = [{ 
              name: '封面图', 
              url: product.image, 
              uid: Date.now(), 
              status: 'success' 
            }];
            formData.image = product.image;
            console.log('封面图处理成功:', product.image);
          }
          
          // 处理商品滚图
          const tempImagesList = [];
          const tempFormImages = [];
          
          // 尝试多种方式处理images数据
          if (product.images) {
            // 方式1: 直接作为数组处理
            if (Array.isArray(product.images)) {
              console.log('方式1: images是数组');
              product.images.forEach((img, index) => {
                let imageUrl = '';
                if (typeof img === 'string') {
                  imageUrl = img;
                } else if (typeof img === 'object' && img !== null && img.url) {
                  imageUrl = img.url;
                }
                
                if (imageUrl) {
                  // 确保图片URL是完整的
                  let previewUrl = imageUrl;
                  if (previewUrl && !previewUrl.startsWith('http://') && !previewUrl.startsWith('https://')) {
                    const baseDomain = window.location.origin;
                    previewUrl = `${baseDomain}${previewUrl}`;
                  }
                  
                  tempImagesList.push({
                    name: `图片${index + 1}`,
                    url: previewUrl,
                    uid: Date.now() + index,
                    status: 'success'
                  });
                  tempFormImages.push(imageUrl);
                }
              });
            }
            // 方式2: 作为字符串处理，尝试解析为JSON
            else if (typeof product.images === 'string') {
              console.log('方式2: images是字符串');
              try {
                // 尝试解析JSON字符串
                const parsedImages = JSON.parse(product.images);
                if (Array.isArray(parsedImages)) {
                  parsedImages.forEach((img, index) => {
                    let imageUrl = '';
                    if (typeof img === 'string') {
                      imageUrl = img;
                    } else if (typeof img === 'object' && img !== null && img.url) {
                      imageUrl = img.url;
                    }
                    
                    if (imageUrl) {
                      let previewUrl = imageUrl;
                      if (previewUrl && !previewUrl.startsWith('http://') && !previewUrl.startsWith('https://')) {
                        const baseDomain = window.location.origin;
                        previewUrl = `${baseDomain}${previewUrl}`;
                      }
                      
                      tempImagesList.push({
                        name: `图片${index + 1}`,
                        url: previewUrl,
                        uid: Date.now() + index,
                        status: 'success'
                      });
                      tempFormImages.push(imageUrl);
                    }
                  });
                }
                // 如果解析后不是数组，尝试作为单个图片URL处理
                else if (typeof parsedImages === 'string') {
                  let imageUrl = parsedImages;
                  let previewUrl = imageUrl;
                  if (previewUrl && !previewUrl.startsWith('http://') && !previewUrl.startsWith('https://')) {
                    const baseDomain = window.location.origin;
                    previewUrl = `${baseDomain}${previewUrl}`;
                  }
                  
                  tempImagesList.push({
                    name: '图片1',
                    url: previewUrl,
                    uid: Date.now(),
                    status: 'success'
                  });
                  tempFormImages.push(imageUrl);
                }
              } catch (e) {
                console.log('解析JSON失败，尝试作为单个图片URL处理:', e);
                // 解析失败，尝试作为单个图片URL处理
                let imageUrl = product.images;
                let previewUrl = imageUrl;
                if (previewUrl && !previewUrl.startsWith('http://') && !previewUrl.startsWith('https://')) {
                  const baseDomain = window.location.origin;
                  previewUrl = `${baseDomain}${previewUrl}`;
                }
                
                tempImagesList.push({
                  name: '图片1',
                  url: previewUrl,
                  uid: Date.now(),
                  status: 'success'
                });
                tempFormImages.push(imageUrl);
              }
            }
            // 方式3: 作为对象处理
            else if (typeof product.images === 'object' && product.images !== null) {
              console.log('方式3: images是对象');
              // 检查是否有url属性
              if (product.images.url) {
                let imageUrl = product.images.url;
                let previewUrl = imageUrl;
                if (previewUrl && !previewUrl.startsWith('http://') && !previewUrl.startsWith('https://')) {
                  const baseDomain = window.location.origin;
                  previewUrl = `${baseDomain}${previewUrl}`;
                }
                
                tempImagesList.push({
                  name: '图片1',
                  url: previewUrl,
                  uid: Date.now(),
                  status: 'success'
                });
                tempFormImages.push(imageUrl);
              }
              // 检查是否有其他可能的图片属性
              else if (product.images.path) {
                let imageUrl = product.images.path;
                let previewUrl = imageUrl;
                if (previewUrl && !previewUrl.startsWith('http://') && !previewUrl.startsWith('https://')) {
                  const baseDomain = window.location.origin;
                  previewUrl = `${baseDomain}${previewUrl}`;
                }
                
                tempImagesList.push({
                  name: '图片1',
                  url: previewUrl,
                  uid: Date.now(),
                  status: 'success'
                });
                tempFormImages.push(imageUrl);
              }
            }
          } else {
            console.log('product.images为null或undefined');
          }
          
          console.log('临时图片列表:', tempImagesList);
          console.log('临时formImages列表:', tempFormImages);
          
          // 强制更新响应式数据
          imagesFileList.value = [];
          formData.images = [];
          
          // 填充其他表单数据
          formData.name = product.name || '';
          formData.intro = product.intro || '';
          formData.description = product.description || '';
          formData.price = product.price || 0;
          formData.original_price = product.original_price || 0;
          formData.category_id = product.category_id || null;
          formData.product_type = product.product_type || 'physical';
          formData.validity_days = product.validity_days || 1;
          formData.status = product.status || 'active';
          formData.sort_order = product.sort_order || 0;
          formData.stock = product.stock || 0;
          formData.sold_count = product.sold_count || 0;
          formData.need_verification = product.need_verification || false;
          formData.support_shipping = product.support_shipping || true;
          formData.is_default = product.is_default || true;
          formData.goods_id = product.goods_id || null;
          
          console.log('product.available_member_levels:', product.available_member_levels);
          console.log('product.available_member_levels类型:', typeof product.available_member_levels);
          console.log('product.available_member_levels是否为数组:', Array.isArray(product.available_member_levels));
          
          // 处理会员等级，确保是数组格式
          if (product.available_member_levels) {
            if (Array.isArray(product.available_member_levels)) {
              console.log('使用数组格式');
              formData.available_member_levels = product.available_member_levels.map(level => String(level));
            } else if (typeof product.available_member_levels === 'string') {
              console.log('使用字符串格式');
              // 如果是字符串，尝试解析为数组
              try {
                formData.available_member_levels = JSON.parse(product.available_member_levels).map(level => String(level));
              } catch (e) {
                console.error('解析available_member_levels失败:', e);
                formData.available_member_levels = [];
              }
            } else {
              console.log('未知格式，设置为空数组');
              formData.available_member_levels = [];
            }
          } else {
            console.log('available_member_levels为空，设置为空数组');
            formData.available_member_levels = [];
          }
          
          console.log('最终formData.available_member_levels:', formData.available_member_levels);
          
          formData.is_limit_purchase = product.is_limit_purchase || false;
          formData.limit_purchase_count = product.limit_purchase_count || 1;
          formData.points_required = product.points_required || 0;
          if (product.id) {
            formData.id = product.id;
          }
          
          console.log('编辑商品时的会员等级:', formData.available_member_levels);
          console.log('编辑商品时的会员等级类型:', typeof formData.available_member_levels);
          console.log('编辑商品时的会员等级是否为数组:', Array.isArray(formData.available_member_levels));
          
          // 等待Vue更新
          setTimeout(() => {
            imagesFileList.value = [...tempImagesList];
            formData.images = [...tempFormImages];
            
            console.log('最终imagesFileList:', imagesFileList.value);
            console.log('最终imagesFileList长度:', imagesFileList.value.length);
            console.log('最终formData.images:', formData.images);
            console.log('最终formData.images长度:', formData.images.length);
            
            // 验证imagesFileList中的每个元素格式
            imagesFileList.value.forEach((img, index) => {
              console.log(`图片${index}格式:`, {
                name: img.name,
                url: img.url,
                uid: img.uid,
                status: img.status,
                hasAllFields: !!img.name && !!img.url && !!img.uid && !!img.status
              });
            });
            
            // 确保所有数据都填充完成后再显示对话框，给Vue足够的时间处理响应式更新
            setTimeout(() => {
              console.log('即将显示对话框，此时的imagesFileList:', imagesFileList.value);
              console.log('即将显示对话框，此时的imagesFileList长度:', imagesFileList.value.length);
              console.log('即将显示对话框，memberLevels:', memberLevels.value);
              console.log('即将显示对话框，formData.available_member_levels:', formData.available_member_levels);
              dialogVisible.value = true;
              activeTab.value = 'basic';
              
              // 对话框显示后再次检查，确保数据正确
              setTimeout(() => {
                console.log('对话框显示后的imagesFileList:', imagesFileList.value);
                console.log('对话框显示后的imagesFileList长度:', imagesFileList.value.length);
                console.log('对话框显示后的memberLevels:', memberLevels.value);
                console.log('对话框显示后的formData.available_member_levels:', formData.available_member_levels);
              }, 300);
            }, 100);
          }, 50);
        } else {
          ElMessage.error('获取商品详情失败');
        }
      } catch (error) {
        ElMessage.error('获取商品详情失败');
        console.error(error);
      }
    };
    
    // 重置表单
    const resetForm = () => {
      Object.assign(formData, {
        name: '',
        intro: '',
        description: '',
        price: 0,
        original_price: 0,
        category_id: null,
        image: '',
        images: [], // 确保初始化为空数组
        product_type: 'physical',
        validity_days: 1,
        status: 'active',
        sort_order: 0,
        stock: 0,
        sold_count: 0,
        need_verification: false,
        support_shipping: true,
        is_default: true,
        goods_id: null,
        available_member_levels: [],
        is_limit_purchase: false,
        limit_purchase_count: 1,
        points_required: 0
      });
      // 清空文件列表
      imageFileList.value = [];
      imagesFileList.value = [];
    };
    
    // 处理表单提交
    const handleSubmit = async () => {
      if (!formRef.value) return;
      
      await formRef.value.validate(async (valid) => {
        if (valid) {
          try {
            let response;
            if (isEditMode.value) {
              response = await productApi.updateExchangeProduct(formData.id, formData);
            } else {
              response = await productApi.createExchangeProduct(formData);
            }
            
            if (response.code === 200) {
              ElMessage.success(isEditMode.value ? '商品更新成功' : '商品创建成功');
              dialogVisible.value = false;
              loadProducts();
            } else {
              ElMessage.error(isEditMode.value ? '商品更新失败' : '商品创建失败');
            }
          } catch (error) {
            ElMessage.error(isEditMode.value ? '商品更新失败' : '商品创建失败');
            console.error(error);
          }
        }
      });
    };
    
    // 处理弹窗关闭
    const handleDialogClose = () => {
      resetForm();
      dialogVisible.value = false;
    };
    
    // 处理图片上传成功
    const handleImageUploadSuccess = (response, file) => {
      // 处理wangEditor格式响应
      if (response.errno === 0 && response.data && response.data.length > 0) {
        const imageUrl = response.data[0].url;
        formData.image = imageUrl;
        imageFileList.value = [{ url: imageUrl }];
        ElMessage.success('封面图上传成功');
        // 手动触发表单验证，确保image字段验证通过
        if (formRef.value) {
          formRef.value.validateField('image');
        }
      } else {
        ElMessage.error(response.message || '封面图上传失败');
      }
    };
    
    // 处理多图上传成功
    const handleImagesUploadSuccess = (response, file) => {
      // 处理wangEditor格式响应
      if (response.errno === 0 && response.data && response.data.length > 0) {
        const imageUrl = response.data[0].url;
        formData.images.push(imageUrl);
        imagesFileList.value.push({ url: imageUrl });
        ElMessage.success('图片上传成功');
      } else {
        ElMessage.error(response.message || '图片上传失败');
      }
    };
    
    // 处理图片上传错误
    const handleImageUploadError = (error, file) => {
      ElMessage.error('图片上传失败');
    };
    
    // 自定义图片上传函数
    const customImageUpload = async (options) => {
      try {
        const { file } = options;
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        // 获取API基础URL和认证token
        const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
        const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
        const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
        const authToken = localStorage.getItem('token');
        
        // 直接使用axios而不是全局request，避免响应拦截器影响
        const axios = await import('axios');
        const response = await axios.default({
          url: `${apiBaseUrl}/manage/upload/image`,
          method: 'post',
          data: uploadFormData,
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
          },
          timeout: 15000
        });
        
        const res = response.data;
        // 处理wangEditor格式响应
        if (res.errno === 0 && res.data && res.data.length > 0) {
          let imageUrl = res.data[0].url;
          // 确保返回的是完整的绝对路径
          if (imageUrl && !imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
            imageUrl = `${baseDomain}${imageUrl}`;
          }
          formData.image = imageUrl;
          imageFileList.value = [{ url: imageUrl }];
          ElMessage.success('封面图上传成功');
          // 手动触发表单验证，确保image字段验证通过
          if (formRef.value) {
            formRef.value.validateField('image');
          }
          options.onSuccess();
        } else {
          ElMessage.error(res.message || '封面图上传失败');
          options.onError();
        }
      } catch (error) {
        console.error('图片上传失败:', error);
        ElMessage.error('封面图上传失败');
        options.onError();
      }
    };
    
    // 清除已上传的图片
    const removeImage = () => {
      formData.image = '';
      imageFileList.value = [];
      // 手动触发表单验证，确保image字段验证失败
      if (formRef.value) {
        formRef.value.validateField('image');
      }
      ElMessage.success('已清除图片，您可以上传新的图片');
    };
    
    // 自定义商品滚图上传函数
    const customImagesUpload = async (options) => {
      try {
        const { file } = options;
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        
        // 获取API基础URL和认证token
        const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
        const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
        const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
        const authToken = localStorage.getItem('token');
        
        // 直接使用axios而不是全局request，避免响应拦截器影响
        const axios = await import('axios');
        const response = await axios.default({
          url: `${apiBaseUrl}/manage/upload/image`,
          method: 'post',
          data: uploadFormData,
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
          },
          timeout: 15000
        });
        
        const res = response.data;
        // 处理wangEditor格式响应
        if (res.errno === 0 && res.data && res.data.length > 0) {
          let imageUrl = res.data[0].url;
          // 确保返回的是完整的绝对路径
          if (imageUrl && !imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
            imageUrl = `${baseDomain}${imageUrl}`;
          }
          
          // 添加到 formData.images
          formData.images.push(imageUrl);
          
          // 添加到 imagesFileList
          const newImage = {
            name: file.name,
            url: imageUrl,
            uid: Date.now(),
            status: 'success'
          };
          imagesFileList.value.push(newImage);
          
          ElMessage.success('图片上传成功');
          options.onSuccess(newImage);
        } else {
          ElMessage.error(res.message || '图片上传失败');
          options.onError();
        }
      } catch (error) {
        console.error('图片上传失败:', error);
        ElMessage.error('图片上传失败');
        options.onError();
      }
    };
    
    // 处理图片移除
    const handleImageRemove = (file, fileList) => {
      // 处理封面图移除
      if (file.url === formData.image) {
        formData.image = '';
        imageFileList.value = [];
        // 手动触发表单验证，确保image字段验证失败
        if (formRef.value) {
          formRef.value.validateField('image');
        }
      }
      // 处理商品滚图移除
      else if (file.url) {
        // 从 formData.images 中移除
        const index = formData.images.findIndex(img => {
          // 处理完整URL和相对URL的情况
          if (img === file.url) {
            return true;
          }
          // 如果 file.url 是完整URL，检查 img 是否是其中的相对路径部分
          if (file.url.includes(img)) {
            return true;
          }
          // 处理相对URL和完整URL的情况
          if (img.includes(file.url)) {
            return true;
          }
          return false;
        });
        if (index > -1) {
          formData.images.splice(index, 1);
        }
        
        // 更新 imagesFileList
        imagesFileList.value = [...fileList];
      }
    };
    
    // 处理图片预览
    const handlePictureCardPreview = (file) => {
      dialogImageUrl.value = file.url;
      previewVisible.value = true;
    };
    
    // 处理状态变更
    const handleStatusChange = async (row) => {
      const newStatus = row.status === 'active' ? 'inactive' : 'active';
      try {
        const response = await productApi.updateProductStatus(row.id, newStatus);
        if (response.code === 200) {
          row.status = newStatus;
          ElMessage.success(`商品已${newStatus === 'active' ? '上架' : '下架'}`);
        } else {
          ElMessage.error('状态更新失败');
        }
      } catch (error) {
        ElMessage.error('状态更新失败');
        console.error(error);
      }
    };
    
    // 删除商品
    const handleDelete = (row) => {
      ElMessageBox.confirm('删除后将无法查看相关兑换记录，是否继续？', '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        try {
          const response = await productApi.deleteExchangeProduct(row.id);
          if (response.code === 200) {
            ElMessage.success('删除成功');
            loadProducts();
          } else {
            ElMessage.error('删除失败');
          }
        } catch (error) {
          ElMessage.error('删除失败');
          console.error(error);
        }
      }).catch(() => {
        // 用户取消删除
      });
    };
    
    // 分页大小变化
    const handleSizeChange = (size) => {
      pagination.pageSize = size;
      loadProducts();
    };

    // 当前页变化
    const handleCurrentChange = (current) => {
      pagination.currentPage = current;
      loadProducts();
    };

    // 获取会员等级列表
    const fetchMemberLevels = async () => {
        loadingMemberLevels.value = true;
        try {
            const response = await fetchMemberLevelOptions();
            console.log('fetchMemberLevels原始响应:', response);
            console.log('fetchMemberLevels响应code:', response.code);
            console.log('fetchMemberLevels响应data:', response.data);
            if (response.code === 200) {
                memberLevels.value = response.data;
                console.log('会员等级列表加载成功:', memberLevels.value);
            } else {
                console.error('会员等级列表加载失败，code:', response.code, 'message:', response.message);
            }
        } catch (error) {
            console.error('获取会员等级失败:', error);
            ElMessage.error('获取会员等级失败');
        } finally {
            loadingMemberLevels.value = false;
        }
    };

    // 组件挂载时加载数据
    onMounted(() => {
      loadProducts();
      fetchMemberLevels();
    });

    return {
      searchForm,
      loading,
      productList,
      pagination,
      dialogVisible,
      dialogTitle,
      isEditMode,
      activeTab,
      formData,
      formRef,
      formRules,
      imageFileList,
      imagesFileList,
      previewVisible,
      dialogImageUrl,
      editor,
      mode,
      toolbarConfig,
      editorConfig,
      handleCreated,
      handleSearch,
      showCreateDialog,
      showEditDialog,
      handleSubmit,
      handleDialogClose,
      handleImageUploadSuccess,
      handleImagesUploadSuccess,
      handleImageUploadError,
      handleImageRemove,
      handlePictureCardPreview,
      customImageUpload,
      customImagesUpload,
      removeImage,
      handleStatusChange,
      handleDelete,
      handleSizeChange,
      handleCurrentChange,
      memberLevels,
      loadingMemberLevels
    };
  }
};
</script>

<style scoped>
.exchange-products {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-area {
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.search-input {
  width: 400px;
}

.search-filters {
  display: flex;
  gap: 10px;
  align-items: center;
}

.product-list-content {
  margin-top: 20px;
}

.product-table {
  margin-bottom: 20px;
}

.pagination-area {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/* 弹窗样式 */
.product-form-content {
  max-height: 600px;
  overflow-y: auto;
  position: relative;
}

.product-form {
  margin-top: 20px;
}

.el-upload {
  margin-bottom: 20px;
}

.rich-text-editor {
  min-height: 200px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

/* WangEditor 工具栏样式 */
.rich-text-editor :deep(.w-e-toolbar) {
  border-bottom: 1px solid #dcdfe6;
}

/* WangEditor 编辑区域样式 */
.rich-text-editor :deep(.w-e-text-container) {
  height: 300px;
  border: none;
}

/* 标签页样式 */
.product-form .el-tabs__content {
  margin-top: 20px;
  padding-top: 70px; /* 为悬浮tab留出空间 */
}

/* 自定义标签页样式 - 悬浮效果 */
.custom-tabs {
  display: flex;
  border-bottom: 1px solid #e4e7ed;
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 100;
  padding: 10px 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 0; /* 移除底部边距，因为sticky定位会覆盖内容 */
}

.custom-tab {
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #606266;
  background-color: #f5f7fa;
  border: 1px solid #e4e7ed;
  margin-right: 10px;
  border-radius: 4px 4px 0 0;
  transition: all 0.3s ease;
}

.custom-tab:hover {
  color: #409EFF;
  background-color: #ecf5ff;
}

.custom-tab.active {
  color: #fff;
  background-color: #409EFF;
  border-color: #409EFF;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 商品信息列样式 */
.product-info-column {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.product-name {
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.product-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.product-meta .el-tag {
  height: auto;
  padding: 2px 8px;
  font-size: 12px;
}

/* 有效期设置样式 */
.validity-unit {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.form-help-text {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.form-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #f56c6c;
  line-height: 1.5;
}

/* 图片必填提示 */
.image-required-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #f56c6c;
  line-height: 1.5;
}
</style>